import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api";
import type { AllianceCoverageRow } from "../../types/allianceCoverage";

interface AlliancesState {
  items: any[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  current: any | null;
  stats: any | null;
  allianceCoverage: AllianceCoverageRow[];
  homeLoading: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AlliancesState = {
  items: [],
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  current: null,
  stats: null,
  allianceCoverage: [],
  homeLoading: false,
  loading: false,
  error: null,
};

export const fetchAlliances = createAsyncThunk(
  "alliances/fetchAll",
  async (params: Record<string, any>) => {
    const response = await api.getAlliances(params);
    return response;
  },
);

export const fetchAllianceById = createAsyncThunk(
  "alliances/fetchById",
  async (id: string) => {
    const response = await api.getAllianceById(id);
    return response;
  },
);

export const fetchHomeDashboard = createAsyncThunk(
  "alliances/fetchHomeDashboard",
  async () => {
    const [stats, allianceCoverage] = await Promise.all([
      api.getStats(),
      api.getCoverage(),
    ]);
    return {
      stats,
      allianceCoverage: allianceCoverage as AllianceCoverageRow[],
    };
  },
);

const alliancesSlice = createSlice({
  name: "alliances",
  initialState,
  reducers: {
    clearCurrent: (state) => {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlliances.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlliances.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchAlliances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Greška pri dohvaćanju saveza";
      })
      .addCase(fetchAllianceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllianceById.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchAllianceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Greška pri dohvaćanju saveza";
      })
      .addCase(fetchHomeDashboard.pending, (state) => {
        state.homeLoading = true;
        state.error = null;
      })
      .addCase(fetchHomeDashboard.fulfilled, (state, action) => {
        state.homeLoading = false;
        state.stats = action.payload.stats;
        state.allianceCoverage = action.payload.allianceCoverage;
      })
      .addCase(fetchHomeDashboard.rejected, (state, action) => {
        state.homeLoading = false;
        state.error =
          action.error.message ||
          "Greška pri dohvaćanju početne nadzorne ploče";
      });
  },
});

export const { clearCurrent } = alliancesSlice.actions;
export const alliancesReducer = alliancesSlice.reducer;
